import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { api, requestOtp, verifyOtp } from "../lib/api";
import { formatApiErrorDetail, useAuth } from "../lib/auth";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("password"); // password | otp
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const nextPath = sp.get("next") || "/account";

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { identifier: identifier.trim(), password });
      setAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name || "friend"}`);
      navigate(nextPath, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    if (otpPhone.trim().length < 6) { toast.error("Enter your phone number"); return; }
    setOtpLoading(true);
    try {
      const d = await requestOtp(otpPhone.trim());
      setOtpSent(true);
      if (d.dev_code) {
        // Mocked mode — show the code so testing works today
        toast.success(`OTP sent to ${d.sent_to}. Test code: ${d.dev_code}`, { duration: 15000 });
      } else {
        toast.success(`OTP sent to ${d.sent_to}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 4) return;
    setOtpLoading(true);
    try {
      const d = await verifyOtp(otpPhone.trim(), otpCode.trim());
      setAuth(d.token, d.user);
      toast.success(`Welcome, ${d.user.name || "friend"}`);
      navigate(nextPath, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="login-page">
      <Seo title="Sign In" description="Sign in to your RIZ N WASH account to book pickups, manage subscriptions and see order history." path="/login" noindex={true} />
      <Nav />
      <div className="max-w-md mx-auto px-6 md:px-10 pt-14">
        <div className="overline mb-3">Sign in</div>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Welcome back.
        </h1>
        <p className="mt-3 text-rw-ink2">Sign in to book pickups, manage subscriptions and pick up where you left off.</p>

        <div className="mt-8 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 space-y-4">
          <button
            onClick={startGoogleAuth}
            className="w-full flex items-center justify-center gap-3 rounded-full border border-rw-ink/20 py-3 hover:bg-rw-bg3 text-sm font-semibold"
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 text-xs text-rw-ink3">
            <div className="flex-1 h-px bg-rw-ink/10" />
            OR
            <div className="flex-1 h-px bg-rw-ink/10" />
          </div>

          <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-rw-bg3" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("password")}
              className={`text-xs py-1.5 rounded-full font-semibold transition-colors ${
                mode === "password" ? "bg-rw-ink text-white" : "text-rw-ink2"
              }`}
              data-testid="login-mode-password"
            >
              Password
            </button>
            <button
              type="button"
              onClick={() => setMode("otp")}
              className={`text-xs py-1.5 rounded-full font-semibold transition-colors ${
                mode === "otp" ? "bg-rw-ink text-white" : "text-rw-ink2"
              }`}
              data-testid="login-mode-otp"
            >
              OTP
            </button>
          </div>

          {mode === "password" ? (
          <form onSubmit={submit} className="space-y-3" data-testid="login-form">
            <label className="block">
              <span className="overline">Email or phone</span>
              <input
                className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or 98xxx xxxxx"
                autoFocus
                data-testid="login-identifier"
              />
            </label>
            <label className="block">
              <span className="overline">Password</span>
              <input
                type="password"
                className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          ) : (
            <>
              {!otpSent ? (
                <form onSubmit={sendOtp} className="space-y-3" data-testid="otp-request-form">
                  <label className="block">
                    <span className="overline">Phone number</span>
                    <input
                      className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                      value={otpPhone}
                      onChange={(e) => setOtpPhone(e.target.value)}
                      placeholder="+91 98xxx xxxxx"
                      autoFocus
                      data-testid="otp-phone-input"
                    />
                  </label>
                  <p className="text-xs text-rw-ink3">We&apos;ll send a 6-digit code via SMS and WhatsApp. Valid for 5 minutes.</p>
                  <button
                    type="submit"
                    disabled={otpLoading}
                    className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
                    data-testid="otp-send-btn"
                  >
                    {otpLoading ? "Sending…" : "Send OTP"}
                  </button>
                </form>
              ) : (
                <form onSubmit={submitOtp} className="space-y-3" data-testid="otp-verify-form">
                  <div className="text-xs text-rw-ink2">Code sent to <span className="font-mono">•••••{otpPhone.slice(-4)}</span></div>
                  <label className="block">
                    <span className="overline">Enter 6-digit code</span>
                    <input
                      inputMode="numeric"
                      maxLength={6}
                      className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-3 text-center text-2xl font-mono tracking-[0.5em] rw-focus"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      autoFocus
                      data-testid="otp-code-input"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={otpLoading || otpCode.length < 4}
                    className="btn-dark w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-40"
                    data-testid="otp-verify-btn"
                  >
                    {otpLoading ? "Verifying…" : "Verify & sign in"}
                  </button>
                  <div className="flex justify-between text-xs">
                    <button type="button" onClick={() => { setOtpSent(false); setOtpCode(""); }} className="text-rw-ink2 hover:text-rw-ink" data-testid="otp-change-phone-btn">
                      ← Change phone
                    </button>
                    <button type="button" onClick={(e) => sendOtp(e)} disabled={otpLoading} className="text-rw-ink2 hover:text-rw-ink" data-testid="otp-resend-btn">
                      Resend code
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
        <p className="mt-6 text-sm text-rw-ink2 text-center">
          New to RIZ N WASH?{" "}
          <Link to={`/register${nextPath && nextPath !== "/account" ? `?next=${encodeURIComponent(nextPath)}` : ""}`} className="text-rw-ink font-semibold hover:underline" data-testid="link-to-register">
            Create an account
          </Link>
        </p>
      </div>
      <Footer />
    </div>
  );
}
